import React, {useEffect, useState} from 'react';
import {CheckIcon, SelectorIcon} from '@heroicons/react/solid'
import {Combobox} from '@headlessui/react'
import {concatenate} from "services/string";
import IconName from "types/IconName";
import Icon from "components/Icon";
import Spinner from "components/Spinner";

export type Item<T = number | string> = {
    type?: string | null
    id: T // id must be unique
    name: string
    description?: string | null
    icon?: IconName | string
    indent?: boolean
}

type SelectorProps<T> = {
    label?: string
    hint?: string
    isLoading?: boolean
    items: Array<T>
    selectedId?: number | string | null
    onChange: (item: T) => any
    showSelected?: boolean
    required?: boolean
    disabled?: boolean
}

function Selector<T extends Item = Item>({label, hint, isLoading = false, items, selectedId, onChange, showSelected = true, ...props}: SelectorProps<T>) {
    const [query, setQuery] = useState<string>('');
    const [selected, setSelected] = useState<Item | null>(null);

    useEffect(() => {
        if (isLoading) return;

        if (selectedId === null) setSelected(null);

        // If there's no selected item yet, try to use selectedId
        if (!selected && (selectedId || selectedId === 0)) {
            const match = items.find(i => i.id === selectedId);
            if (match) setSelected(match);

            return;
        }

        // If selected item exists but is no longer a valid reference in the items array, update or clear it
        if (selected) {
            const updated = items.find(i => i.id === selected.id) ?? null;
            if (updated !== selected) setSelected(updated);
        }
    }, [items, isLoading, selected, selectedId]);

    function handleChange(item: T) {
        if(showSelected) setSelected(item)

        onChange(item)
    }

    const filteredItems = (query === '') ? items : items.filter((item) => {
        const queryWords = query.toLowerCase().split(' ').filter(word => !!word)
        const itemText   = `${item.name.toLowerCase()} ${item.description?.toLowerCase()}`

        for(let i = 0; i < queryWords.length; i ++){
            if(!itemText.includes(queryWords[i])){
                return false
            }
        }

        return true;
    })

    const spinner = (
        <Spinner color={Spinner.colors.BRAND} size={Spinner.sizes.X_SMALL}/>
    )

    const input = (<>
        <Combobox.Input
            className={concatenate(props.disabled && 'text-gray-300', 'w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand sm:text-sm')}
            onChange={(event) => setQuery(event.target.value)}
            displayValue={(item?: Item) => !isLoading ? (item?.name || '') : ''}
            autoComplete="off"
            required={!!props.required}
        />
        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            {isLoading ? spinner : <SelectorIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
        </Combobox.Button>
    </>)

    return (
        <Combobox as="div" value={selected} onChange={handleChange} disabled={props.disabled}>
            <div className="flex flex-col gap-1">
                {!!label && <Combobox.Label className="block text-sm font-medium text-gray-700">{label}</Combobox.Label>}
                {!!hint && <span className="text-sm text-gray-500">{hint}</span>}
            </div>


            <div className={concatenate(label? 'mt-1' : null, 'relative')}>
                {input}

                {(filteredItems.length > 0) && (
                    <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {filteredItems.map((item) => (
                            <Combobox.Option
                                key={item.id}
                                value={item}
                                className={({ active }) =>
                                    concatenate(
                                        'relative cursor-default select-none py-2 pl-3 pr-9',
                                        active ? 'bg-brand text-white' : 'text-gray-900'
                                    )
                                }
                            >
                                {({ active, selected }) => (
                                    <>
                                        <div className="flex">
                                            <span className={concatenate('truncate flex flex-shrink-0 items-center', selected && 'font-semibold')}>
                                                {item.indent && <span className="ml-2 mr-2">↳</span>}
                                                {item.icon && <Icon name={item.icon} className={concatenate(active? 'text-gray-100' : 'text-gray-500', 'hidden sm:block h-4 w-4 mr-2 flex-shrink-0')}/>}
                                                {item.name}
                                            </span>

                                            {item.description &&
                                                <span
                                                    className={concatenate(
                                                        'ml-4 sm:ml-2 truncate',
                                                        active ? 'text-gray-200' : 'text-gray-500'
                                                    )}
                                                >
                                                    {item.description}
                                                </span>
                                            }
                                        </div>

                                        {selected && (
                                            <span className={concatenate(
                                                'absolute inset-y-0 right-0 flex items-center pr-4',
                                                active ? 'text-white' : 'text-brand'
                                            )}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                        )}
                                    </>
                                )}
                            </Combobox.Option>
                        ))}
                    </Combobox.Options>
                )}
            </div>
        </Combobox>
    );
}

export default Selector;